import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiUsers, 
  FiMessageSquare, 
  FiEye, 
  FiShare2, 
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiUser,
  FiCpu,
  FiClock,
  FiStar
} from 'react-icons/fi';

const TeamChatGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: FiUsers,
      title: 'Real-time Collaboration',
      description: 'Team members can see AI conversations as they happen, enabling instant collaboration and feedback.',
      benefits: ['Live conversation viewing', 'Instant team notifications', 'Seamless collaboration flow']
    },
    {
      icon: FiMessageSquare,
      title: 'Multi-AI Chat',
      description: 'Chat with multiple AI models simultaneously while your team observes and contributes to the conversation.',
      benefits: ['Multiple AI perspectives', 'Comparative responses', 'Enhanced decision making']
    },
    {
      icon: FiEye,
      title: 'Live Visibility',
      description: 'Everyone on your team can see the AI conversation in real-time, making collaboration transparent and efficient.',
      benefits: ['Complete transparency', 'No missed context', 'Improved team alignment']
    },
    {
      icon: FiShare2,
      title: 'Instant Sharing',
      description: 'Share insights, build on ideas, and make decisions together as AI responses come in.',
      benefits: ['Quick feedback loops', 'Collaborative insights', 'Faster decision making']
    }
  ];

  const steps = [
    {
      step: 1,
      title: 'Create or Join a Team',
      description: 'Set up your team workspace or join an existing one using an invitation link.',
      icon: FiUsers,
      details: [
        'Navigate to Team Settings in your Bervin dashboard',
        'Click "Create New Team" or enter an invitation code',
        'Set team permissions and member roles',
        'Invite team members via email or sharing a link'
      ]
    },
    {
      step: 2,
      title: 'Start a Team AI Session',
      description: 'Begin a new AI conversation that your team can view and participate in.',
      icon: FiMessageSquare,
      details: [
        'Click "New Team Chat" from your dashboard',
        'Select which AI models you want to include',
        'Choose conversation visibility settings',
        'Start chatting - your team will see everything in real-time'
      ]
    },
    {
      step: 3,
      title: 'Collaborate in Real-time',
      description: 'Watch as team members see your AI conversations live and contribute their insights.',
      icon: FiEye,
      details: [
        'Team members receive instant notifications',
        'Everyone can see the conversation as it unfolds',
        'Team members can add comments and suggestions',
        'Build on AI responses together for better outcomes'
      ]
    }
  ];

  const mockMessages = [
    {
      id: 1,
      type: 'user',
      author: 'You',
      message: 'How can we improve our product\'s user onboarding experience?',
      timestamp: '2:34 PM',
      viewers: ['Alice', 'Bob', 'Carol']
    },
    {
      id: 2,
      type: 'ai',
      author: 'Claude AI',
      message: 'Here are several strategies to improve your onboarding experience:\n\n1. **Progressive disclosure** - Show features gradually\n2. **Interactive tutorials** - Let users learn by doing\n3. **Personalization** - Tailor the experience to user roles\n4. **Clear progress indicators** - Show users where they are in the process',
      timestamp: '2:34 PM',
      viewers: ['Alice', 'Bob', 'Carol']
    },
    {
      id: 3,
      type: 'team',
      author: 'Alice',
      message: '💡 The progressive disclosure approach sounds great! We could implement this in our current flow.',
      timestamp: '2:35 PM'
    },
    {
      id: 4,
      type: 'ai',
      author: 'GPT-4',
      message: 'Additionally, consider implementing:\n\n• **Contextual help** - Provide assistance exactly when needed\n• **Success celebrations** - Acknowledge user achievements\n• **Feedback loops** - Ask for input during the process\n• **Easy exit/re-entry** - Allow users to pause and return',
      timestamp: '2:35 PM',
      viewers: ['Alice', 'Bob', 'Carol']
    },
    {
      id: 5,
      type: 'team',
      author: 'Bob',
      message: 'The feedback loops idea is excellent. We should definitely implement user surveys at key moments.',
      timestamp: '2:36 PM'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-3 mb-8"
          >
            <Link 
              to="/guide" 
              className="text-gray-400 hover:text-white transition-colors duration-200 flex items-center"
            >
              <FiArrowLeft className="w-4 h-4 mr-2" />
              Guide
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-secondary-400">Team Chat with AI</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiUsers className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">Team Chat with AI</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Collaborate with your team in real-time while chatting with AI. Everyone can see the conversation 
              as it happens, share insights, and build on ideas together for better outcomes.
            </p>

            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center space-x-2 text-gray-400">
                <FiClock className="w-5 h-5" />
                <span>5 min read</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <FiStar className="w-5 h-5" />
                <span>Featured Guide</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold mb-4 gradient-text">
              See It in Action
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watch how team chat with AI works in real-time
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-dark-800/80 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden">
              {/* Chat Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-semibold">Team Chat: Product Strategy</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {['Alice', 'Bob', 'Carol'].map((name, index) => (
                      <div
                        key={name}
                        className="w-8 h-8 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full flex items-center justify-center text-sm font-semibold"
                        style={{ marginLeft: index > 0 ? '-0.5rem' : '0' }}
                      >
                        {name[0]}
                      </div>
                    ))}
                    <span className="text-sm text-gray-400 ml-2">3 watching</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live</span>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="h-96 overflow-y-auto p-6 space-y-4">
                {mockMessages.map((msg, index) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.2 }}
                    className={`flex items-start space-x-3 ${
                      msg.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      msg.type === 'ai' ? 'bg-gradient-to-r from-purple-500 to-blue-500' :
                      msg.type === 'user' ? 'bg-gradient-to-r from-secondary-500 to-accent-500' :
                      'bg-gradient-to-r from-green-500 to-emerald-500'
                    }`}>
                      {msg.type === 'ai' ? <FiCpu className="w-4 h-4" /> : <FiUser className="w-4 h-4" />}
                    </div>
                    
                    <div className={`flex-1 ${msg.type === 'user' ? 'text-right' : ''}`}>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-semibold text-gray-300">{msg.author}</span>
                        <span className="text-xs text-gray-500">{msg.timestamp}</span>
                        {msg.viewers && (
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <FiEye className="w-3 h-3" />
                            <span>{msg.viewers.length}</span>
                          </div>
                        )}
                      </div>
                      <div className={`p-3 rounded-lg max-w-md ${
                        msg.type === 'ai' ? 'bg-purple-900/30 border border-purple-500/20' :
                        msg.type === 'user' ? 'bg-secondary-900/30 border border-secondary-500/20 ml-auto' :
                        'bg-green-900/30 border border-green-500/20'
                      }`}>
                        <p className="text-sm text-gray-200 whitespace-pre-line">{msg.message}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Chat Input */}
              <div className="p-6 border-t border-white/10">
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-dark-700/50 rounded-lg px-4 py-3 border border-white/10">
                    <span className="text-gray-400">Type your message or question for AI...</span>
                  </div>
                  <button className="px-6 py-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-lg font-semibold text-white hover:scale-105 transition-transform duration-200">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Key Features
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover what makes team chat with AI so powerful for collaboration
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-secondary-500/50 transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="p-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl mr-4">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-white">
                      {feature.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center text-gray-300">
                        <FiCheck className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              How to Get Started
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these simple steps to start collaborating with your team using AI
            </p>
          </motion.div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 * index }}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
              >
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-xl font-bold text-white mr-4">
                      {step.step}
                    </div>
                    <h3 className="text-3xl font-display font-bold text-white">
                      {step.title}
                    </h3>
                  </div>
                  
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <ul className="space-y-3">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start text-gray-300">
                        <FiArrowRight className="w-5 h-5 text-secondary-400 mr-3 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex-1 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-3xl blur-xl"></div>
                    <div className="relative w-80 h-64 bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-3xl flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-secondary-400" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Try Team Chat with AI?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Experience the power of real-time AI collaboration with your team. 
              Start chatting with AI while your team watches and contributes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href="https://bervin.appiworks.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Start Team Chat Now
              </a>
              <Link
                to="/guide"
                className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
              >
                Back to Guides
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamChatGuide;
